/** @format */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
	compareQuestionNumbers,
	REJECTION_REASON_MAX_LENGTH,
	type Brand,
	type InheritedPaperFields,
	type Paper,
	type ParentGroupSummary,
	type Question,
	type SearchParams,
} from "@question-bank/shared";
import { getPaper } from "../api/papers";
import { getQuestion, updateQuestion } from "../api/questions";
import { Locale } from "../features/i18n/init";
import { buildParentGroupSummary } from "../utils/parentSummary";
import { AdminQuestionTable } from "./AdminQuestionTable";
import { notify } from "./Toast/toast";

/** Page size for this table. Matches `DEFAULT_LIMIT` in AdminQuestionsPage. */
const PAGE_LIMIT = 20;

const paperFieldsOf = (paper: Paper | null): InheritedPaperFields | undefined =>
	paper
		? {
				subject: paper.subject,
				qualification: paper.qualification,
				tier: paper.tier,
				series: paper.series,
				date: paper.date,
			}
		: undefined;

/**
 * One row per parent group, the same shape the Questions list search returns.
 * A part id stays inside its parent. Marks and parts are summed there.
 */
const groupDraftQuestions = (questions: Question[], paperFields?: InheritedPaperFields): ParentGroupSummary[] => {
	const groups = new Map<string, Question[]>();
	for (const question of questions) {
		const key = question.parentQuestionId || question.id;
		const items = groups.get(key) ?? [];
		items.push(question);
		groups.set(key, items);
	}
	return [...groups.values()]
		.map((items) => buildParentGroupSummary(items, paperFields))
		.sort((left, right) => compareQuestionNumbers(left.questionNumber, right.questionNumber));
};

interface ImportDraftQuestionsProps {
	brand: Brand;
	locale: Locale | undefined;
	jobId: string;
	paperId?: string;
	paperCode?: string;
	createdQuestionIds: string[];
	skippedIds: ReadonlySet<string>;
}

interface ReturnToDraftDialogProps {
	isReturning: boolean;
	reason: string;
	stagedCount: number;
	onReasonChange: (reason: string) => void;
	onConfirm: () => void;
	onCancel: () => void;
}

/**
 * Staged to draft is a rejection. The update API requires a reason, an expert,
 * and a person other than the one who staged the question.
 */
const ReturnToDraftDialog = ({
	isReturning,
	reason,
	stagedCount,
	onReasonChange,
	onConfirm,
	onCancel,
}: ReturnToDraftDialogProps) => {
	const cancelRef = useRef<HTMLButtonElement>(null);
	const dialogRef = useRef<HTMLDivElement>(null);
	const canConfirm = reason.trim().length > 0 && !isReturning;

	useEffect(() => {
		cancelRef.current?.focus();
	}, []);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape" && !isReturning) {
				onCancel();
			}
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onCancel, isReturning]);

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) {
			return;
		}
		const focusable = dialog.querySelectorAll<HTMLElement>(
			'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		);
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const trap = (event: KeyboardEvent) => {
			if (event.key !== "Tab" || !first || !last) {
				return;
			}
			if (event.shiftKey ? document.activeElement === first : document.activeElement === last) {
				event.preventDefault();
				(event.shiftKey ? last : first).focus();
			}
		};
		dialog.addEventListener("keydown", trap);
		return () => dialog.removeEventListener("keydown", trap);
	}, [isReturning]);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby="return-draft-title"
				aria-describedby="return-draft-desc"
				className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-lg"
			>
				<h2 id="return-draft-title" className="text-base font-semibold text-slate-800">
					Return these questions to draft?
				</h2>
				<p id="return-draft-desc" className="mt-2 text-sm text-slate-600">
					{stagedCount === 1
						? "1 staged question goes back to draft. Published questions stay as they are. A reason is required."
						: `${stagedCount} staged questions go back to draft. Published questions stay as they are. A reason is required.`}
				</p>
				<label htmlFor="draft-return-reason" className="mt-4 block text-xs font-semibold text-slate-600">
					Rejection reason
				</label>
				<textarea
					id="draft-return-reason"
					value={reason}
					onChange={(event) => onReasonChange(event.target.value)}
					required
					rows={3}
					maxLength={REJECTION_REASON_MAX_LENGTH}
					className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800"
				/>
				<div className="mt-5 flex justify-end gap-3">
					<button
						ref={cancelRef}
						type="button"
						onClick={onCancel}
						disabled={isReturning}
						className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={onConfirm}
						disabled={!canConfirm}
						className="rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-40"
					>
						{isReturning ? "Returning to draft…" : "Confirm return to draft"}
					</button>
				</div>
			</div>
		</div>
	);
};

export const ImportDraftQuestions = ({
	brand,
	locale,
	jobId,
	paperId,
	paperCode,
	createdQuestionIds,
	skippedIds,
}: ImportDraftQuestionsProps) => {
	const [groups, setGroups] = useState<ParentGroupSummary[]>([]);
	const [questions, setQuestions] = useState<Question[]>([]);
	const [missingIds, setMissingIds] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(createdQuestionIds.length > 0);
	const [error, setError] = useState<string | null>(null);
	const [offset, setOffset] = useState(0);
	const [reloadToken, setReloadToken] = useState(0);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isReturning, setIsReturning] = useState(false);
	const [reason, setReason] = useState("");
	const returnButtonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		setOffset(0);
	}, [createdQuestionIds]);

	useEffect(() => {
		if (createdQuestionIds.length === 0) {
			setGroups([]);
			setQuestions([]);
			setMissingIds([]);
			setError(null);
			setIsLoading(false);
			return;
		}

		let isCancelled = false;
		const load = async () => {
			setIsLoading(true);
			setError(null);
			try {
				const settled = await Promise.allSettled(
					createdQuestionIds.map((questionId) => getQuestion(brand, questionId)),
				);
				if (isCancelled) {
					return;
				}
				const loaded: Question[] = [];
				const missing: string[] = [];
				settled.forEach((result, index) => {
					if (result.status === "fulfilled") {
						loaded.push(result.value);
					} else {
						missing.push(createdQuestionIds[index]);
					}
				});
				const paper = paperId ? await getPaper(brand, paperId) : null;
				if (isCancelled) {
					return;
				}
				if (loaded.length === 0) {
					setQuestions([]);
					setGroups([]);
					setMissingIds(missing);
					setError("Failed to load questions");
					return;
				}
				setQuestions(loaded);
				setGroups(groupDraftQuestions(loaded, paperFieldsOf(paper)));
				setMissingIds(missing);
			} catch (err) {
				if (!isCancelled) {
					setError(err instanceof Error ? err.message : "Failed to load questions");
				}
			} finally {
				if (!isCancelled) {
					setIsLoading(false);
				}
			}
		};

		void load();
		return () => {
			isCancelled = true;
		};
	}, [brand, paperId, createdQuestionIds, reloadToken]);

	const stagedQuestions = questions.filter((question) => question.status === "staged");
	const keptIds = createdQuestionIds.filter((questionId) => skippedIds.has(questionId));
	const page = groups.slice(offset, offset + PAGE_LIMIT);
	const listFilters: SearchParams = {
		limit: PAGE_LIMIT,
		offset: 0,
		includeDrafts: true,
		...(paperCode ? { paperCode } : {}),
	};
	const questionsListState = paperCode ? { filters: listFilters } : undefined;
	const tableFilters: SearchParams = { ...listFilters, offset };

	const closeDialog = () => {
		if (isReturning) {
			return;
		}
		setIsDialogOpen(false);
		returnButtonRef.current?.focus();
	};

	const handleReturnToDraft = async () => {
		const rejectionReason = reason.trim();
		if (!rejectionReason || stagedQuestions.length === 0) {
			return;
		}
		setIsReturning(true);
		try {
			for (const question of stagedQuestions) {
				await updateQuestion(question.id, brand, { status: "draft", rejectionReason });
			}
			const count = stagedQuestions.length;
			notify("success", count === 1 ? "Returned 1 question to draft" : `Returned ${count} questions to draft`);
			setReason("");
			setIsDialogOpen(false);
			setReloadToken((token) => token + 1);
		} catch (err) {
			notify("error", err instanceof Error ? err.message : "Return to draft failed. Please try again");
		} finally {
			setIsReturning(false);
		}
	};

	return (
		<section aria-labelledby="draft-questions-heading">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<h3 id="draft-questions-heading" className="text-base font-semibold text-slate-800">
					Draft Questions
				</h3>
				{createdQuestionIds.length > 0 && (
					<div className="flex flex-wrap gap-2">
						<Link
							to={`/${locale}/admin/import-jobs/${jobId}/review`}
							className="inline-flex items-center rounded-md bg-(--admin-color-primary) px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
						>
							Review and accept
						</Link>
						{stagedQuestions.length > 0 && (
							<button
								ref={returnButtonRef}
								type="button"
								onClick={() => setIsDialogOpen(true)}
								className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
							>
								Return to draft
							</button>
						)}
					</div>
				)}
			</div>

			{createdQuestionIds.length === 0 ? (
				<p className="mt-2 text-sm text-slate-600">No questions were created.</p>
			) : (
				<>
					<Link
						to={`/${locale}/admin/questions`}
						state={questionsListState}
						className="mt-2 inline-block text-sm font-semibold text-(--admin-color-primary-strong) hover:underline"
					>
						{paperCode ? "View these questions in the Questions list" : "View the Questions list"}
					</Link>
					<div className="mt-3">
						<AdminQuestionTable
							questions={page}
							total={groups.length}
							limit={PAGE_LIMIT}
							offset={offset}
							isLoading={isLoading}
							error={error}
							filters={tableFilters}
							onPageChange={setOffset}
							onRetry={() => setReloadToken((token) => token + 1)}
						/>
					</div>
					{missingIds.length > 0 && !error && (
						<p className="mt-2 text-sm text-slate-600">Could not load {missingIds.join(", ")}.</p>
					)}
					{keptIds.length > 0 && (
						<ul className="mt-2 space-y-1 text-sm">
							{keptIds.map((questionId) => (
								<li key={questionId}>
									<code className="text-slate-800">{questionId}</code>
									<span className="ml-1 text-slate-600">(kept - edited, published or not yet written)</span>
								</li>
							))}
						</ul>
					)}
				</>
			)}

			{isDialogOpen && (
				<ReturnToDraftDialog
					isReturning={isReturning}
					reason={reason}
					stagedCount={stagedQuestions.length}
					onReasonChange={setReason}
					onConfirm={() => void handleReturnToDraft()}
					onCancel={closeDialog}
				/>
			)}
		</section>
	);
};
